import { eventsApiBaseUrl } from "../config/eventsApi";
import { galleryAlbums } from "../data/galleryData";
import { eventFlyerSrc } from "./publicEventsApi";

const FETCH_TIMEOUT_MS = 8000;

/** Absolute URL, `/api/...` on the API origin, or a site path such as `/Gallery/...`. */
export function resolveMediaUrl(url, apiBase = eventsApiBaseUrl()) {
  return eventFlyerSrc(url, apiBase);
}

function positiveSize(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return amount;
}

function slugify(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function normalizePhoto(photo, index, title) {
  if (typeof photo === "string") {
    const url = resolveMediaUrl(photo);
    if (!url) return null;
    return {
      id: `p-${index}`,
      url,
      width: null,
      height: null,
      alt: title || "",
      caption: "",
    };
  }
  if (!photo || typeof photo !== "object") return null;
  const url = resolveMediaUrl(photo.url || photo.src || "");
  if (!url) return null;
  const alt =
    typeof photo.alt === "string" && photo.alt.trim() ? photo.alt.trim() : title || "";
  return {
    id: photo.id != null && String(photo.id).trim() ? String(photo.id) : `p-${index}`,
    url,
    width: positiveSize(photo.width),
    height: positiveSize(photo.height),
    alt,
    caption: typeof photo.caption === "string" ? photo.caption.trim() : "",
  };
}

export function normalizeAlbum(raw) {
  if (!raw || typeof raw !== "object") return null;
  const title = typeof raw.title === "string" ? raw.title.trim() : "";
  if (!title) return null;
  const photos = (Array.isArray(raw.photos) ? raw.photos : [])
    .map((photo, index) => normalizePhoto(photo, index, title))
    .filter(Boolean);
  if (photos.length === 0) return null;
  const coverRaw =
    raw.cover && typeof raw.cover === "object"
      ? raw.cover.url
      : raw.cover || raw.coverWebp || "";
  const coverUrl = resolveMediaUrl(coverRaw) || photos[0].url;
  const slug = slugify(raw.slug || raw.folder || title);
  if (!slug) return null;
  const photoCount = positiveSize(raw.photoCount) || photos.length;
  return {
    id: raw.id != null && String(raw.id).trim() ? String(raw.id) : slug,
    slug,
    title,
    date: typeof raw.date === "string" ? raw.date : "",
    category: typeof raw.category === "string" ? raw.category : "",
    description: typeof raw.description === "string" ? raw.description : "",
    eventSlug:
      typeof raw.eventSlug === "string" && raw.eventSlug.trim()
        ? raw.eventSlug.trim()
        : null,
    cover: {
      url: coverUrl,
      width: positiveSize(raw.cover?.width),
      height: positiveSize(raw.cover?.height),
    },
    photoCount,
    photos,
  };
}

/** Bundled albums, mapped into the public API shape. */
export function fallbackAlbums(albums = galleryAlbums) {
  return (Array.isArray(albums) ? albums : []).map(normalizeAlbum).filter(Boolean);
}

/** API albums when the list has anything; otherwise the bundled gallery. */
export function selectAlbums(apiAlbums, bundled = fallbackAlbums()) {
  const albums = (Array.isArray(apiAlbums) ? apiAlbums : [])
    .map(normalizeAlbum)
    .filter(Boolean);
  return albums.length > 0 ? albums : bundled;
}

async function readJson(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function fetchJson(path, signal) {
  const base = eventsApiBaseUrl();
  if (!base) return null;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  const onAbort = () => ctrl.abort();
  signal?.addEventListener("abort", onAbort);
  try {
    const res = await fetch(`${base}${path}`, {
      signal: ctrl.signal,
      headers: { Accept: "application/json" },
    });
    const data = await readJson(res);
    if (!res.ok || !data || data.ok !== true) return null;
    return data;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
}

/** Albums for the homepage gallery. Never empty when bundled photos exist. */
export async function fetchPublicGallery({ signal } = {}) {
  const data = await fetchJson("/api/public/gallery", signal);
  return selectAlbums(data?.albums);
}

/** One album by slug. Falls back to the list, then the bundled gallery. */
export async function fetchPublicGalleryAlbum(slug, { signal } = {}) {
  const wanted = slugify(slug);
  if (!wanted) return null;
  const data = await fetchJson(
    `/api/public/gallery/${encodeURIComponent(wanted)}`,
    signal,
  );
  const direct = data?.album ? normalizeAlbum(data.album) : null;
  if (direct && direct.slug === wanted) return direct;
  const albums = await fetchPublicGallery({ signal });
  return albums.find((album) => album.slug === wanted) || null;
}
